import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Divider,
  Skeleton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Tooltip,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  InputAdornment,
} from '@mui/material';
import {
  Email,
  Send,
  Preview,
  Science,
  People,
  CheckBox,
  CheckBoxOutlineBlank,
  IndeterminateCheckBox,
  History,
  Search,
  Refresh,
} from '@mui/icons-material';
import adminService from '../../services/adminService';
import ResponsiveTableContainer from './shared/ResponsiveTableContainer';
import { useConfirm } from '../Common/ConfirmDialog';

const PROVIDER_STATUSES = [
  { value: 'APPROVED', label: 'Elfogadott', color: 'success' },
  { value: 'PENDING', label: 'Függőben', color: 'warning' },
  { value: 'REJECTED', label: 'Elutasított', color: 'error' },
  { value: 'SUSPENDED', label: 'Felfüggesztett', color: 'default' },
];

const LEAD_STATUSES = [
  { value: 'NEW', label: 'Új', color: 'primary' },
  { value: 'CONTACTED', label: 'Megkeresett', color: 'warning' },
  { value: 'CONVERTED', label: 'Konvertált', color: 'success' },
  { value: 'REJECTED', label: 'Elutasított', color: 'error' },
];

const LOG_STATUS_CONFIG = {
  SENT: { label: 'Elküldve', color: 'success' },
  FAILED: { label: 'Sikertelen', color: 'error' },
  PENDING: { label: 'Függőben', color: 'default' },
};

// ─── Email küldő szekció ──────────────────────────────────────────────────────

function SendSection() {
  const [confirm, confirmDialog] = useConfirm();
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [recipientCounts, setRecipientCounts] = useState(null);

  const [includeClients, setIncludeClients] = useState(false);
  const [selectedProviderStatuses, setSelectedProviderStatuses] = useState([]);
  const [selectedLeadStatuses, setSelectedLeadStatuses] = useState([]);
  const [includeUnregisteredLeads, setIncludeUnregisteredLeads] = useState(false);

  const [testEmail, setTestEmail] = useState('');
  const [manualEmail, setManualEmail] = useState('');
  const [manualName, setManualName] = useState('');
  const [manualSendLoading, setManualSendLoading] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [sendLoading, setSendLoading] = useState(false);
  const [alert, setAlert] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [recipientList, setRecipientList] = useState(null);
  const [recipientListLoading, setRecipientListLoading] = useState(false);
  const [checkedEmails, setCheckedEmails] = useState(new Set());

  useEffect(() => {
    const load = async () => {
      try {
        const [tmplList, counts] = await Promise.all([
          adminService.getBulkEmailTemplates(),
          adminService.getBulkEmailRecipients(),
        ]);
        setTemplates(tmplList);
        setRecipientCounts(counts);
        if (tmplList.length > 0) setSelectedTemplateId(tmplList[0].id);
      } catch {
        setAlert({ severity: 'error', message: 'Hiba az adatok betöltésekor.' });
      }
    };
    load();
  }, []);

  const selectedTemplate = templates.find(t => t.id === selectedTemplateId);

  const toggleProviderStatus = (status) => {
    setSelectedProviderStatuses(prev =>
      prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]
    );
  };

  const toggleLeadStatus = (status) => {
    setSelectedLeadStatuses(prev =>
      prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]
    );
  };

  const estimatedCount = useMemo(() => {
    if (!recipientCounts) return 0;
    let count = 0;
    if (includeClients) count += recipientCounts.clients || 0;
    for (const s of selectedProviderStatuses) count += recipientCounts.providers?.[s] || 0;
    for (const s of selectedLeadStatuses) count += recipientCounts.provider_leads?.[s] || 0;
    if (includeUnregisteredLeads) count += recipientCounts.unregistered_leads || 0;
    return count;
  }, [recipientCounts, includeClients, selectedProviderStatuses, selectedLeadStatuses, includeUnregisteredLeads]);

  const handlePreview = useCallback(async () => {
    if (!selectedTemplateId) return;
    setPreviewLoading(true);
    try {
      const html = await adminService.previewBulkEmail(selectedTemplateId);
      setPreviewHtml(html);
    } catch {
      setAlert({ severity: 'error', message: 'Hiba az előnézet generálásakor.' });
    } finally {
      setPreviewLoading(false);
    }
  }, [selectedTemplateId]);

  const handleTestSend = async () => {
    if (!testEmail || !selectedTemplateId) return;
    setTestLoading(true);
    setAlert(null);
    try {
      await adminService.sendTestBulkEmail(selectedTemplateId, testEmail);
      setAlert({ severity: 'success', message: `Teszt email elküldve: ${testEmail}` });
    } catch (err) {
      setAlert({ severity: 'error', message: err?.message || 'Hiba a teszt email küldésekor.' });
    } finally {
      setTestLoading(false);
    }
  };

  const handleManualSend = async () => {
    if (!manualEmail || !selectedTemplateId) return;
    if (!(await confirm({ title: 'Élő email küldése', message: `Biztosan kiküldi az élő emailt a(z) "${selectedTemplate?.name}" sablonnal erre a címre: ${manualEmail}?`, confirmLabel: 'Küldés' }))) {
      return;
    }
    setManualSendLoading(true);
    setAlert(null);
    try {
      const result = await adminService.sendBulkEmail(selectedTemplateId, null, [
        { email: manualEmail, name: manualName, businessName: '' },
      ]);
      setAlert({ severity: 'success', message: result?.message || `Email elküldve: ${manualEmail}` });
      setManualEmail('');
      setManualName('');
    } catch (err) {
      setAlert({ severity: 'error', message: err?.message || 'Hiba az email küldésekor.' });
    } finally {
      setManualSendLoading(false);
    }
  };

  const buildRecipientGroups = () => ({
    ...(includeClients && { clients: true }),
    ...(selectedProviderStatuses.length > 0 && { providers: selectedProviderStatuses }),
    ...(selectedLeadStatuses.length > 0 && { provider_leads: selectedLeadStatuses }),
    ...(includeUnregisteredLeads && { unregistered_leads: true }),
  });

  const openConfirmDialog = async () => {
    setRecipientListLoading(true);
    setRecipientList(null);
    setCheckedEmails(new Set());
    try {
      const result = await adminService.listBulkEmailRecipients(buildRecipientGroups());
      const list = result?.recipients || [];
      setRecipientList(list);
      setCheckedEmails(new Set(list.map(r => r.email)));
    } catch {
      setAlert({ severity: 'error', message: 'Hiba a címzett lista betöltésekor.' });
    } finally {
      setRecipientListLoading(false);
      setConfirmDialogOpen(true);
    }
  };

  const toggleEmail = (email) => {
    setCheckedEmails(prev => {
      const next = new Set(prev);
      next.has(email) ? next.delete(email) : next.add(email);
      return next;
    });
  };

  const allChecked = recipientList && checkedEmails.size === recipientList.length;
  const someChecked = checkedEmails.size > 0 && !allChecked;

  const toggleAll = () => {
    if (allChecked) {
      setCheckedEmails(new Set());
    } else {
      setCheckedEmails(new Set(recipientList.map(r => r.email)));
    }
  };

  const handleBulkSend = async () => {
    setConfirmDialogOpen(false);
    setSendLoading(true);
    setAlert(null);
    try {
      const selectedRecipients = (recipientList || []).filter(r => checkedEmails.has(r.email));
      const result = await adminService.sendBulkEmail(selectedTemplateId, null, selectedRecipients);
      setAlert({
        severity: 'success',
        message: result?.message || `${result?.sent}/${result?.total} email sikeresen elküldve.`,
      });
    } catch (err) {
      setAlert({ severity: 'error', message: err?.message || 'Hiba a tömeges küldés során.' });
    } finally {
      setSendLoading(false);
    }
  };

  const sourceChipColor = (source) => {
    if (source === 'provider') return 'success';
    if (source === 'client') return 'info';
    if (source === 'unregistered_lead') return 'warning';
    return 'default';
  };

  return (
    <Box>
      {alert && (
        <Alert severity={alert.severity} onClose={() => setAlert(null)} sx={{ mb: 2 }}>
          {alert.message}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Bal panel – konfiguráció */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} mb={2} display="flex" alignItems="center" gap={1}>
                <Email fontSize="small" /> Sablon
              </Typography>

              <FormControl fullWidth size="small" sx={{ mb: 3 }}>
                <InputLabel>Email sablon</InputLabel>
                <Select
                  value={selectedTemplateId}
                  label="Email sablon"
                  onChange={e => setSelectedTemplateId(e.target.value)}
                >
                  {templates.map(t => (
                    <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {selectedTemplate && (
                <Typography variant="body2" color="text.secondary" mb={3}>
                  {selectedTemplate.description}
                </Typography>
              )}

              <Divider sx={{ mb: 2 }} />

              <Typography variant="subtitle1" fontWeight={600} mb={2} display="flex" alignItems="center" gap={1}>
                <People fontSize="small" /> Célközönség
              </Typography>

              {/* A) Kliensek */}
              <Typography variant="body2" color="text.secondary" mb={0.5} fontWeight={500}>
                Kliensek
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox checked={includeClients} onChange={e => setIncludeClients(e.target.checked)} size="small" />
                }
                label={
                  <Typography variant="body2">
                    Összes kliens ({recipientCounts?.clients ?? '—'} db)
                  </Typography>
                }
                sx={{ mb: 2 }}
              />

              {/* B) Regisztrált szolgáltatók */}
              <Typography variant="body2" color="text.secondary" mb={0.5} fontWeight={500}>
                Regisztrált szolgáltatók
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                {PROVIDER_STATUSES.map(s => {
                  const count = recipientCounts?.providers?.[s.value] ?? '—';
                  const checked = selectedProviderStatuses.includes(s.value);
                  return (
                    <Chip
                      key={s.value}
                      label={`${s.label} (${count})`}
                      color={checked ? s.color : 'default'}
                      variant={checked ? 'filled' : 'outlined'}
                      onClick={() => toggleProviderStatus(s.value)}
                      clickable
                      size="small"
                    />
                  );
                })}
              </Box>

              {/* C) Előregisztrált leadek */}
              <Typography variant="body2" color="text.secondary" mb={0.5} fontWeight={500}>
                Előregisztrált érdeklődők
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                {LEAD_STATUSES.map(s => {
                  const count = recipientCounts?.provider_leads?.[s.value] ?? '—';
                  const checked = selectedLeadStatuses.includes(s.value);
                  return (
                    <Chip
                      key={s.value}
                      label={`${s.label} (${count})`}
                      color={checked ? s.color : 'default'}
                      variant={checked ? 'filled' : 'outlined'}
                      onClick={() => toggleLeadStatus(s.value)}
                      clickable
                      size="small"
                    />
                  );
                })}
              </Box>

              {/* D) Még nem regisztrált leadek */}
              <Typography variant="body2" color="text.secondary" mb={0.5} fontWeight={500}>
                Előregisztrált – még nem regisztrált
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeUnregisteredLeads}
                    onChange={e => setIncludeUnregisteredLeads(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2">
                    Leadek akik még nem regisztráltak ({recipientCounts?.unregistered_leads ?? '—'} db)
                  </Typography>
                }
                sx={{ mb: 2 }}
              />

              <Box sx={{ bgcolor: '#f5f1e6', borderLeft: '4px solid #edbe5f', borderRadius: '0 6px 6px 0', p: 1.5, mb: 2 }}>
                <Typography variant="body2" fontWeight={600} color="#1b331f">
                  Becsült összesen: {estimatedCount} email
                </Typography>
              </Box>

              <Divider sx={{ mb: 2 }} />

              <Typography variant="subtitle1" fontWeight={600} mb={1} display="flex" alignItems="center" gap={1}>
                <Science fontSize="small" /> Teszt küldés
              </Typography>

              <Box display="flex" gap={1} mb={3}>
                <TextField
                  size="small"
                  label="Teszt email cím"
                  type="email"
                  value={testEmail}
                  onChange={e => setTestEmail(e.target.value)}
                  fullWidth
                />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleTestSend}
                  disabled={!testEmail || !selectedTemplateId || testLoading}
                  startIcon={testLoading ? <CircularProgress size={14} /> : <Send fontSize="small" />}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Teszt
                </Button>
              </Box>

              <Button
                variant="contained"
                fullWidth
                disabled={estimatedCount === 0 || !selectedTemplateId || sendLoading || recipientListLoading}
                onClick={openConfirmDialog}
                startIcon={recipientListLoading ? <CircularProgress size={16} color="inherit" /> : <Send />}
                sx={{ bgcolor: '#1b331f', '&:hover': { bgcolor: '#2a5d32' } }}
              >
                {recipientListLoading ? 'Betöltés...' : `Kiküldés (${estimatedCount} db)`}
              </Button>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" fontWeight={600} mb={1} display="flex" alignItems="center" gap={1}>
                <Email fontSize="small" /> Egyedi címzett (élő küldés)
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={1.5}>
                Kiküldés egyetlen, tetszőleges email címre — nem kell szerepelnie a fenti célközönség-listákban. Ez valódi email, nem teszt.
              </Typography>
              <Box display="flex" flexDirection="column" gap={1}>
                <TextField
                  size="small"
                  label="Email cím"
                  type="email"
                  value={manualEmail}
                  onChange={e => setManualEmail(e.target.value)}
                  fullWidth
                />
                <TextField
                  size="small"
                  label="Név (megszólításhoz)"
                  value={manualName}
                  onChange={e => setManualName(e.target.value)}
                  fullWidth
                />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleManualSend}
                  disabled={!manualEmail || !selectedTemplateId || manualSendLoading}
                  startIcon={manualSendLoading ? <CircularProgress size={14} /> : <Send fontSize="small" />}
                >
                  Küldés erre a címre
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Jobb panel – előnézet */}
        <Grid size={{ xs: 12, md: 7 }}>
          <Card variant="outlined" sx={{ height: '100%', minHeight: 500 }}>
            <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle1" fontWeight={600} display="flex" alignItems="center" gap={1}>
                  <Preview fontSize="small" /> Előnézet
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handlePreview}
                  disabled={!selectedTemplateId || previewLoading}
                  startIcon={previewLoading ? <CircularProgress size={14} /> : <Preview fontSize="small" />}
                >
                  Előnézet frissítése
                </Button>
              </Box>

              {previewLoading ? (
                <Box>
                  <Skeleton variant="rectangular" height={60} sx={{ mb: 1 }} />
                  <Skeleton variant="rectangular" height={200} sx={{ mb: 1 }} />
                  <Skeleton variant="rectangular" height={40} />
                </Box>
              ) : previewHtml ? (
                <iframe
                  srcDoc={previewHtml}
                  title="Email előnézet"
                  style={{ flex: 1, border: 'none', width: '100%', minHeight: 480, borderRadius: 4 }}
                />
              ) : (
                <Box display="flex" alignItems="center" justifyContent="center" flex={1} color="text.secondary">
                  <Typography variant="body2">Kattints az „Előnézet frissítése" gombra.</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Megerősítő dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { height: '80vh', display: 'flex', flexDirection: 'column' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          Kiküldés megerősítése
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Sablon: <strong>{selectedTemplate?.name}</strong>
          </Typography>
        </DialogTitle>

        <Box
          sx={{
            px: 3,
            py: 1,
            borderTop: '1px solid',
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: 'grey.50',
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip title={allChecked ? 'Mindet töröl' : 'Mindet kijelöl'}>
              <IconButton size="small" onClick={toggleAll}>
                {allChecked
                  ? <CheckBox fontSize="small" color="primary" />
                  : someChecked
                    ? <IndeterminateCheckBox fontSize="small" color="primary" />
                    : <CheckBoxOutlineBlank fontSize="small" />
                }
              </IconButton>
            </Tooltip>
            <Typography variant="body2" color="text.secondary">
              {checkedEmails.size} / {recipientList?.length ?? 0} kijelölve
            </Typography>
          </Box>
          <Button size="small" onClick={toggleAll} sx={{ textTransform: 'none', fontSize: 12 }}>
            {allChecked ? 'Mindet töröl' : 'Mindet kijelöl'}
          </Button>
        </Box>

        <DialogContent sx={{ p: 0, overflowY: 'auto', flex: 1 }}>
          {recipientList && (
            <List dense disablePadding>
              {recipientList.map((r, i) => (
                <ListItem
                  key={r.email}
                  divider={i < recipientList.length - 1}
                  disablePadding
                  secondaryAction={
                    <Chip
                      label={r.sourceLabel}
                      size="small"
                      color={sourceChipColor(r.source)}
                      variant="outlined"
                      sx={{ fontSize: 11, mr: 1 }}
                    />
                  }
                  sx={{ opacity: checkedEmails.has(r.email) ? 1 : 0.45 }}
                >
                  <ListItemIcon sx={{ minWidth: 40, pl: 1 }}>
                    <Checkbox
                      edge="start"
                      size="small"
                      checked={checkedEmails.has(r.email)}
                      onChange={() => toggleEmail(r.email)}
                      tabIndex={-1}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={r.email}
                    secondary={`${r.name || ''}${r.businessName ? ` · ${r.businessName}` : ''}`}
                    primaryTypographyProps={{ fontSize: 13 }}
                    secondaryTypographyProps={{ fontSize: 12 }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>

        {checkedEmails.size > 50 && (
          <Box px={3} pt={1}>
            <Alert severity="warning" sx={{ py: 0.5 }}>
              Nagy mennyiségű email – a küldés eltarthat néhány percig.
            </Alert>
          </Box>
        )}

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setConfirmDialogOpen(false)}>Mégse</Button>
          <Button
            onClick={handleBulkSend}
            variant="contained"
            disabled={checkedEmails.size === 0}
            sx={{ bgcolor: '#1b331f', '&:hover': { bgcolor: '#2a5d32' } }}
          >
            Kiküldés ({checkedEmails.size} db)
          </Button>
        </DialogActions>
      </Dialog>
      {confirmDialog}
    </Box>
  );
}

// ─── Email Log szekció ────────────────────────────────────────────────────────

function LogSection() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await adminService.getEmailLogs({
        page: page + 1,
        limit: rowsPerPage,
        status: statusFilter,
        search,
      });
      setLogs(result?.logs || []);
      setTotal(result?.total || 0);
    } catch {
      setError('Hiba a logok betöltésekor.');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, statusFilter, search]);

  useEffect(() => { load(); }, [load]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(0);
    setSearch(searchInput);
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('hu-HU', { dateStyle: 'short', timeStyle: 'short' });
  };

  return (
    <Box>
      {/* Szűrők */}
      <Box display="flex" gap={2} mb={2} alignItems="center" flexWrap="wrap">
        <form onSubmit={handleSearchSubmit} style={{ display: 'flex', gap: 8, flex: 1, minWidth: 200 }}>
          <TextField
            size="small"
            placeholder="Email cím vagy tárgy..."
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>,
            }}
            fullWidth
          />
          <Button type="submit" variant="outlined" size="small">Keresés</Button>
        </form>

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Státusz</InputLabel>
          <Select
            value={statusFilter}
            label="Státusz"
            onChange={e => { setStatusFilter(e.target.value); setPage(0); }}
          >
            <MenuItem value="">Mind</MenuItem>
            <MenuItem value="SENT">Elküldve</MenuItem>
            <MenuItem value="FAILED">Sikertelen</MenuItem>
            <MenuItem value="PENDING">Függőben</MenuItem>
          </Select>
        </FormControl>

        <Tooltip title="Frissítés">
          <IconButton size="small" onClick={load} disabled={loading}>
            <Refresh fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <ResponsiveTableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Időpont</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Recipient</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Tárgy</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Státusz</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Hibakód</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              [...Array(5)].map((_, i) => (
                <TableRow key={i}>
                  {[...Array(5)].map((__, j) => (
                    <TableCell key={j}><Skeleton /></TableCell>
                  ))}
                </TableRow>
              ))
            ) : logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary', fontSize: 13 }}>
                  Nincs találat.
                </TableCell>
              </TableRow>
            ) : (
              logs.map(log => {
                const statusCfg = LOG_STATUS_CONFIG[log.status] || { label: log.status, color: 'default' };
                return (
                  <TableRow key={log.id} hover>
                    <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>{formatDate(log.sentAt || log.createdAt)}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{log.recipientEmail}</TableCell>
                    <TableCell sx={{ fontSize: 12, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {log.subject}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={statusCfg.label}
                        color={statusCfg.color}
                        size="small"
                        sx={{ fontSize: 11 }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: 11, color: 'error.main', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {log.errorMessage || '—'}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </ResponsiveTableContainer>

      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
        rowsPerPageOptions={[25, 50, 100]}
        labelRowsPerPage="Sor/oldal:"
        labelDisplayedRows={({ from, to, count }) => `${from}–${to} / ${count}`}
      />
    </Box>
  );
}

// ─── Főkomponens ──────────────────────────────────────────────────────────────

export default function AdminBulkEmailPage() {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <Box>
      <Typography variant="h5" fontFamily="'Playfair Display', serif" color="#1b331f" mb={2}>
        Tömeges email küldő
      </Typography>

      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Küldés" icon={<Send fontSize="small" />} iconPosition="start" />
        <Tab label="Küldési előzmények" icon={<History fontSize="small" />} iconPosition="start" />
      </Tabs>

      {activeTab === 0 && <SendSection />}
      {activeTab === 1 && <LogSection />}
    </Box>
  );
}
